// Which seats get a side panel (CIN-15126, phase 5): the seats at the end of a row and beside an aisle. One rule for
// the studio and the guest map. Plan mm; pure, no imports.

/** What the rule needs of a seat. */
export type EndSeat<K> = { key: K; rowName: string; x: number; width: number };

/** A gap wider than this (mm) between two neighbors in a row is an aisle: both seats beside it get a panel. */
export const AISLE_GAP_MM = 250;

/**
 * For every seat, the sides that face open space: the outer side of a row's first and last seat, and both sides of
 * a gap in the row. Seats are ordered by x within their row, so a curved row works as well as a straight one.
 * Seats without a row name get panels on neither side, and a row of one seat gets both.
 */
export function seatEndPanels<K>(seats: readonly EndSeat<K>[]): Map<K, { left: boolean; right: boolean }> {
    const out = new Map<K, { left: boolean; right: boolean }>();
    const rows = new Map<string, EndSeat<K>[]>();
    for (const seat of seats) {
        if (seat.rowName === '') {
            out.set(seat.key, { left: false, right: false });
            continue;
        }
        const row = rows.get(seat.rowName) ?? [];
        row.push(seat);
        rows.set(seat.rowName, row);
    }
    for (const row of rows.values()) {
        row.sort((a, b) => a.x - b.x);
        row.forEach((seat, i) => {
            const before = row[i - 1];
            const after = row[i + 1];
            const gap = (a: EndSeat<K>, b: EndSeat<K>) => b.x - a.x - (a.width + b.width) / 2;
            out.set(seat.key, {
                left: !before || gap(before, seat) > AISLE_GAP_MM,
                right: !after || gap(seat, after) > AISLE_GAP_MM
            });
        });
    }
    return out;
}
